import {
    type ComponentType,
    createContext,
    useContext,
    useEffect,
    type FC
} from "react";
import type {
    AlertPartContentProps,
    AlertContextType,
    AlertPartKind
} from "./type";
import {
    warnOnce
} from "../../utils/development";

export const ALERT_PART_NAMES: Record<AlertPartKind, string> = {
    description: "Description",
    actions: "Actions",
    title: "Title"
};

export const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const useAlertContext = ({
    part
}: {
    part: AlertPartKind;
}): AlertContextType | undefined => {
    const context = useContext(AlertContext);

    useEffect(() => {
        if(context) {
            return;
        }

        warnOnce({
            message: `Alert.${ALERT_PART_NAMES[part]}: must be rendered inside <Alert>.`
        });
    }, [
        context,
        part
    ]);

    return context;
};

export const withAlertContext = <P extends object>(
    part: AlertPartKind,
    Content: ComponentType<AlertPartContentProps<P>>
): FC<P> & {
    alertPart: AlertPartKind;
} => {
    const AlertPartBase: FC<P> = (props) => {
        const context = useAlertContext({
            part
        });

        if(!context) {
            return null;
        }

        return <Content
            {...props}
            context={context}
        />;
    };

    AlertPartBase.displayName = `Alert.${ALERT_PART_NAMES[part]}`;

    return Object.assign(AlertPartBase, {
        alertPart: part
    });
};
